<template>
  <div
    id="actions_tab"
    class="flex flex-col gap-6 proxima_nova max-w-4xl mx-auto w-full h-full overflow-y-auto"
  >
    <VoiceAgentSectionHeader
      :title="tabHeaders.actions.title"
      :description="tabHeaders.actions.description"
    />

    <div
      v-if="actions.length === 0 && !selectedActionType"
      id="actions_empty_state"
    >
      <CustomContainer :showAddButton="false">
        <div
          class="flex flex-col items-center justify-center gap-6 min-h-[320px]"
        >
          <div class="flex flex-col items-center gap-2">
            <ThemeImage
              :icon="imageNames.EMPTY_SCREEN_GREY"
              alt="actions_icon"
              class="size-16"
            />
            <span class="text-base font-semibold">Actions</span>
            <span class="text-sm secondary_color text-center">
              Add actions your agent should perform during the call
            </span>
          </div>
          <AddItemDropdown
            button-text="Add Action"
            :menu-items="actionMenuItems"
            :show-description="false"
            @select="handleAddAction"
          />
        </div>
      </CustomContainer>
    </div>

    <div
      v-else-if="selectedActionType"
      id="actions_form_container"
      class="flex flex-col gap-4"
    >
      <ActionForm
        :config="selectedActionConfig"
        :action-data="editingAction"
        :is-edit="editingIndex !== null"
        :agent-id="formData.agent_id"
        @save="handleSaveEdit"
        @delete="handleDeleteFromEdit"
      />
    </div>

    <div
      v-else-if="actions.length > 0"
      id="actions_list"
      class="flex flex-col gap-4"
    >
      <VoiceAgentItemCard
        v-for="(action, index) in actions"
        :key="`action_${action.id || index}`"
        :icon="getActionIcon(action.type)"
        icon-alt="action_icon"
        :title="action.name"
        :subtitle="getActionSubtitle(action)"
        :date="action.created_at"
        @edit="handleEditAction(index)"
        @delete="handleDeleteAction(index)"
      />

      <div class="flex justify-center">
        <AddItemDropdown
          button-text="Add Action"
          :menu-items="actionMenuItems"
          :show-description="false"
          @select="handleAddAction"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { message } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import VoiceAgentSectionHeader from "@/src/components/voice_agents/VoiceAgentSectionHeader.vue";
import ActionForm from "@/src/components/voice_agents/actions/ActionForm.vue";
import VoiceAgentItemCard from "@/src/components/voice_agents/VoiceAgentItemCard.vue";
import AddItemDropdown from "@/src/components/voice_agents/AddItemDropdown.vue";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  actionMenuItems,
  actionTypes,
  tabHeaders,
  voiceAgentActionTypes,
} from "@/src/constants/voiceAgentConstants.js";

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:formData"]);
const selectedActionType = ref(null);
const selectedActionConfig = ref(null);
const editingIndex = ref(null);
const editingAction = ref(null);
const isDeleting = ref(false);

const actions = computed(() => props.formData.actions || []);

const getActionIcon = computed(() => (type) => {
  const config = Object.values(actionTypes).find(
    (item) => item.type === type
  );
  return config ? config.icon : imageNames.CALENDAR_TICK_GREY;
});

const getActionSubtitle = (action) => {
  try {
    if (action.type === voiceAgentActionTypes.SCHEDULE_MEETING) {
      return action.trigger_prompt || "Schedule meeting action";
    } else if (action.type === voiceAgentActionTypes.QUALIFICATION_AGENT) {
      return action.qualification_criteria || "Qualification action";
    }
    return "";
  } catch (error) {
    console.error("Error getting action subtitle:", error);
    return "";
  }
};

const closeForm = () => {
  try {
    selectedActionType.value = null;
    selectedActionConfig.value = null;
    editingIndex.value = null;
    editingAction.value = null;
  } catch (error) {
    console.error("Error closing form:", error);
  }
};

const openForm = (config, action = null, index = null) => {
  try {
    selectedActionType.value = config.type;
    selectedActionConfig.value = config;
    editingIndex.value = index;
    editingAction.value = action ? { ...action } : null;
  } catch (error) {
    console.error("Error opening form:", error);
  }
};

const updateActions = (updatedActions) => {
  try {
    emit("update:formData", {
      ...props.formData,
      actions: updatedActions,
    });
  } catch (error) {
    console.error("Error updating actions:", error);
  }
};

const handleAddAction = (config) => {
  try {
    openForm(config);
  } catch (error) {
    console.error("Error adding action:", error);
  }
};

const handleEditAction = (index) => {
  try {
    const action = actions.value[index];
    const config = Object.values(actionTypes).find(
      (item) => item.type === action.type
    );
    openForm(config, action, index);
  } catch (error) {
    console.error("Error editing action:", error);
  }
};

const handleSaveEdit = (actionData) => {
  try {
    const updatedActions = [...actions.value];

    if (editingIndex.value !== null) {
      updatedActions[editingIndex.value] = {
        ...actionData,
        created_at:
          updatedActions[editingIndex.value]?.created_at ||
          actionData.created_at,
      };
    } else {
      updatedActions.push(actionData);
    }

    updateActions(updatedActions);
    closeForm();
  } catch (error) {
    console.error("Error saving action:", error);
  }
};

const handleDeleteFromEdit = async () => {
  try {
    if (editingIndex.value !== null) {
      await handleDeleteAction(editingIndex.value);
    }
    closeForm();
  } catch (error) {
    console.error("Error deleting from edit:", error);
  }
};

const handleDeleteAction = async (index) => {
  try {
    const actionToDelete = actions.value[index];
    if (!actionToDelete) {
      message.error("Action not found");
      return;
    }

    if (!actionToDelete.id) {
      const updatedActions = [...actions.value];
      updatedActions.splice(index, 1);
      updateActions(updatedActions);
      message.success("Unsaved action removed");
      return;
    }

    isDeleting.value = true;
    const response = await VoiceAgentService.deleteVoiceAction(actionToDelete.id);

    if (response?.success) {
      const updatedActions = [...actions.value];
      updatedActions.splice(index, 1);
      updateActions(updatedActions);
      message.success("Action deleted successfully");
    } 
  } catch (error) {
    console.error("Error deleting action:", error);
    message.error("Failed to delete action");
  } finally {
    isDeleting.value = false;
  }
};

const handleContinue = async () => {
  try {
    return true;
  } catch (error) {
    console.error("Error in actions tab:", error);
    return false;
  }
};

defineExpose({
  handleContinue,
  isDeleting,
});
</script>
